import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { groupSoundscapes, soundscapesQueryOptions } from '@/entities/soundscape'
import { cn } from '@/shared/lib'
import { Icon } from '@/shared/ui/icon'
import { Menu, MenuItem } from '@/shared/ui/menu'
import { currentGroup, startInGroup, stepInGroup } from '../lib/catalog'
import { useSoundscapePlayer } from '../model/player-store'
import { useResumePlay } from '../model/use-resume-play'

function PlayerButton({
  icon,
  label,
  onClick,
  active = false,
  pulse = false,
}: {
  icon: 'play' | 'stop' | 'chevron-left' | 'chevron-right'
  label: string
  onClick: () => void
  active?: boolean
  pulse?: boolean
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      className={cn(
        'inline-flex size-8 shrink-0 items-center justify-center rounded-control transition-colors hover:bg-bg-elevated focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-accent',
        active ? 'text-accent' : 'text-text-secondary hover:text-text-primary',
      )}
    >
      <Icon name={icon} size={16} className={cn(pulse && 'animate-pulse')} />
    </button>
  )
}

// Мини-плеер шапки: звук фокуса под рукой на любой странице — старт/стоп и
// переключение режима внутри выбранного раздела. Каталог целиком и помодоро
// живут на странице фокуса, туда ведёт название играющего режима.
export function SoundscapeMiniPlayer() {
  const { data } = useQuery(soundscapesQueryOptions)
  const { slug, playing, loading, lastSlug, group, setGroup, stop } = useSoundscapePlayer()
  const resume = useResumePlay()

  const groups = groupSoundscapes(data ?? [])
  const current = currentGroup(groups, group, slug)
  // Каталог не настроен (или ещё не доехал) — плеера в шапке просто нет.
  if (!current) return null

  const items = current.items
  const playingItem = items.find((s) => s.slug === slug)
  const startTarget = startInGroup(items, lastSlug)
  // Пока молчим — показываем то, что запустится: у кнопки должно быть имя ещё до
  // нажатия, иначе непонятно, что именно включится.
  const shown = playingItem ?? startTarget

  const onToggle = () => {
    if (playing) {
      stop()
      return
    }
    if (startTarget) resume.play(startTarget.slug, startTarget.title)
  }

  const step = (delta: 1 | -1) => {
    const target = stepInGroup(items, slug, delta)
    if (target) resume.play(target.slug, target.title)
  }

  return (
    // Подписи слева, кнопки справа и последними в ряду: группа шапки прижата к
    // правому краю, поэтому от длины названия ездит всё, что стоит левее него —
    // управление на краю остаётся на месте. Ширина названия фиксирована по той же
    // причине: смена режима не должна двигать соседей.
    <div className="flex min-w-0 items-center gap-0.5">
      {/* Название — ссылка на консоль фокуса: там весь каталог, помодоро и
          статистика. Ниже md в шапке места нет, режим читается на самой консоли. */}
      <Link
        to="/focus"
        title="Открыть фокус"
        className="hidden w-28 shrink-0 truncate rounded-control px-1 text-right text-xs text-text-secondary transition-colors hover:text-text-primary md:block"
      >
        {shown?.title ?? 'Фокус'}
      </Link>

      <Menu
        align="end"
        ariaLabel={`Раздел режимов: ${current.title}`}
        className="mr-1 hidden md:inline-flex"
        triggerClassName="text-xs text-text-tertiary hover:text-text-primary"
        caret
        trigger={current.title}
      >
        {(close) => (
          <>
            {groups.map((g) => (
              <MenuItem
                key={g.key}
                selected={g.key === current.key}
                onSelect={() => {
                  setGroup(g.key)
                  close()
                }}
              >
                {g.title}
              </MenuItem>
            ))}
          </>
        )}
      </Menu>

      <PlayerButton icon="chevron-left" label="Предыдущий режим" onClick={() => step(-1)} />
      <PlayerButton
        icon={playing ? 'stop' : 'play'}
        label={playing ? `Выключить «${shown?.title ?? ''}»` : `Включить «${shown?.title ?? ''}»`}
        onClick={onToggle}
        active={playing}
        pulse={loading}
      />
      <PlayerButton icon="chevron-right" label="Следующий режим" onClick={() => step(1)} />
    </div>
  )
}
